import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col } from 'react-bootstrap';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { Formik, Form, FieldArray } from 'formik';
import { Input } from 'reactstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { InputFormFiled } from '../../../components/Common/FormikField/InputFormFiled';
import toastAlert from '../../../js/Base/ToastAlert';
import CloseIcon from '@mui/icons-material/Close';
import { updateConfigKeyApi } from '../../../api/keyApi';
import {
  GCP_KEY_NAME,
  getConfigKeyFields,
  mapGcpJsonToArray,
  normalizeContentsToSpec,
  normalizeKeyName,
} from './configKeyFields';

/** 與原 KeyContents 表格「刪除」相同：文字連結式按鈕 */
const KEY_CONTENTS_ROW_BTN_STYLE = {
  color: '#323232',
  fontSize: '13px',
  fontWeight: 'bold',
  padding: '0px',
};

const CloudConfigKeyEditModal = ({ isModalOpen, handleClose, submitEffect, isCreate, configKey, source }) => {
  const [gcpJsonText, setGcpJsonText] = useState('');
  const [initialValues, setInitialValues] = useState({
    key_name: '',
    project_no: '',
    project_uuid: '',
    remarks: '',
    expiry_date: new Date(),
    key_contents_json_array: [],
  });

  useEffect(() => {
    if (configKey) {
      const editingKeyName = normalizeKeyName(configKey.key_name);
      let jsonArray = Object.entries(configKey.key_contents_json || {}).map(([key, value]) => ({
        key,
        value,
      }));
      // 有固定欄位定義的雲別一律對齊定義，讓編輯時的欄位與順序和新增一致
      jsonArray = normalizeContentsToSpec(editingKeyName, jsonArray);
      setGcpJsonText(
        editingKeyName === GCP_KEY_NAME
          ? JSON.stringify(configKey.key_contents_json || {}, null, 2)
          : ''
      );
      setInitialValues({
        key_name: configKey.key_name,
        project_no: configKey.project_no,
        project_uuid: configKey.project_uuid,
        remarks: configKey.remarks,
        expiry_date: configKey.expiry_date,
        key_contents_json_array: jsonArray,
      });
    }
  }, [configKey]);

  useEffect(() => {
    if (!isModalOpen) {
      setInitialValues({
        key_name: '',
        project_no: '',
        project_uuid: '',
        remarks: '',
        expiry_date: new Date(),
        key_contents_json_array: [],
      });
      setGcpJsonText('');
    }
  }, [isModalOpen]);

  const resetForm = () => {
    setInitialValues({
      key_name: '',
      project_no: '',
      project_uuid: '',
      remarks: '',
      expiry_date: new Date(),
      key_contents_json_array: [],
    });
    setGcpJsonText('');
  };

  const handleSubmit = async (values) => {
    try {
      const newKeyContents = {};
      (values.key_contents_json_array || []).forEach(pair => {
        if (pair.key.trim() !== '') {
          newKeyContents[pair.key] = pair.value;
        }
      });
      configKey.key_contents_json = newKeyContents;
  
      let expiryDateISO = null;
      if (values.expiry_date) {
        const dateObj = typeof values.expiry_date === "string"
          ? new Date(values.expiry_date)
          : values.expiry_date;
        expiryDateISO = dateObj.toISOString();
      }
      const userName = localStorage.getItem('user_email');
      const payload = {
        // 一個專案可能有多把同名 key（例如多把 GCP），以 uuid 定位要更新哪一把
        config_key_uuid: configKey.uuid || configKey.id,
        key_name: values.key_name,
        project_no: values.project_no,
        project_uuid: values.project_uuid,
        remarks: values.remarks,
        expiry_date: expiryDateISO,
        status: 1,
        source: source,
        key_contents_json: configKey.key_contents_json,
        acct_email: userName,
      };
  
      const data = await updateConfigKeyApi(payload);
      if (data.success) {
        toastAlert.autoClose(true).showSuccess("編輯成功");
        resetForm();
        handleClose();
        if (submitEffect) submitEffect();
      } else {
        // 判斷是否重複 key
        if (data.msg && typeof data.msg === "string" && data.msg.toLowerCase().includes("duplicate key value violates unique constraint")) {
          toastAlert.autoClose(true).showError("無法重複建立 Config Key");
        } else {
          toastAlert.autoClose(true).showError(data.msg || "編輯失敗");
        }
        resetForm();
        handleClose();
      }

    } catch (err) {
      if (err.__handled) return;
      toastAlert.autoClose(true).showError("發生錯誤，請稍後再試");
    }
  };

  const handleParseGcpJson = (setFieldValue) => {
    try {
      const parsed = JSON.parse(gcpJsonText);
      setFieldValue('key_contents_json_array', mapGcpJsonToArray(parsed));
      toastAlert.autoClose(true).showSuccess("GCP JSON 解析成功");
    } catch (error) {
      toastAlert.autoClose(true).showError("GCP JSON 格式錯誤，請確認內容");
    }
  };

  return (
    <>
      <Modal
        animation="true"
        style={{ zIndex: '1299' }}
        show={isModalOpen}
        onHide={handleClose}
        className="modal-lg ProjectManagement"
      >
        <Modal.Header>
          <Modal.Title className="modal-title">編輯 Config Key</Modal.Title>
          {/* 解決籃框問題，自訂MUI按鈕 */}
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              marginLeft: 'auto'
            }}
          >
            <CloseIcon fontSize="medium" />
          </button>
        </Modal.Header>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ setFieldValue, handleChange, isSubmitting, errors, touched, values }) => {
            const isGcpKey = normalizeKeyName(values.key_name) === GCP_KEY_NAME;
            const isFixedFieldsKey = Boolean(getConfigKeyFields(values.key_name));
            return (
            <Form>
              <Modal.Body className="modal-body">
                <TableContainer component={Paper} style={{ marginTop: '30px' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ backgroundColor: '#f2f2f2', width: '40%' }}>PropertyName</TableCell>
                        <TableCell style={{ backgroundColor: '#f2f2f2', width: '40%' }}>Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row" style={{ width: '25%' }}>
                          KeyName
                        </TableCell>
                        <TableCell>
                          <InputFormFiled
                            title=""
                            className="input-field custom-select"
                            name="key_name"
                            type="text"
                            disabled={true}
                            invalid={errors.key_name && touched.key_name}
                          />
                        </TableCell>
                      </TableRow>
                      {/* <TableRow>
                        <TableCell component="th" scope="row">
                          專案編號
                        </TableCell>
                        <TableCell>
                          <InputFormFiled
                            title=""
                            className="input-field custom-select"
                            name="project_no"
                            type="text"
                            disabled={true}
                            invalid={errors.project_no && touched.project_no}
                          />
                        </TableCell>
                      </TableRow> */}
                      <TableRow>
                        <TableCell component="th" scope="row">
                          到期日
                        </TableCell>
                        <TableCell>
                          <DatePicker
                            selected={values.expiry_date}
                            onChange={(date) => setFieldValue("expiry_date", date)}
                            showTimeInput
                            timeInputLabel="Time:"
                            dateFormat="yyyy-MM-dd HH:mm:ss"
                            className="form-control"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row">
                          備註
                        </TableCell>
                        <TableCell>
                          <InputFormFiled
                            title=""
                            className="input-field custom-select"
                            name="remarks"
                            type="text"
                            invalid={errors.remarks && touched.remarks}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <div style={{ marginTop: '40px' }}>
                  <h5 style={{ fontWeight: 'bold' }}>KeyContentsJSON</h5>
                  {isGcpKey && (
                    <>
                      <Input
                        type="textarea"
                        rows={6}
                        value={gcpJsonText}
                        onChange={(e) => setGcpJsonText(e.target.value)}
                        placeholder='可直接貼上整包 GCP service account JSON'
                      />
                      <Button
                        type="button"
                        variant="text"
                        style={{ ...KEY_CONTENTS_ROW_BTN_STYLE, marginTop: '10px', marginBottom: '10px' }}
                        onClick={() => handleParseGcpJson(setFieldValue)}
                      >
                        解析 JSON
                      </Button>
                    </>
                  )}
                  <FieldArray name="key_contents_json_array">
                    {({ push, remove }) => (
                      <>
                        <TableContainer component={Paper}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell style={{ backgroundColor: '#f2f2f2', width: isFixedFieldsKey ? '50%' : '40%' }}>Key</TableCell>
                                <TableCell style={{ backgroundColor: '#f2f2f2', width: isFixedFieldsKey ? '50%' : '40%' }}>Value</TableCell>
                                {!isFixedFieldsKey && (
                                  <TableCell style={{ backgroundColor: '#f2f2f2', width: '20%' }}>動作</TableCell>
                                )}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {values.key_contents_json_array &&
                                values.key_contents_json_array.length > 0 &&
                                values.key_contents_json_array.map((pair, index) => (
                                  <TableRow key={index}>
                                    <TableCell>
                                      <Input
                                        type="text"
                                        name={`key_contents_json_array[${index}].key`}
                                        value={pair.key}
                                        onChange={handleChange}
                                        placeholder="Key"
                                        disabled={isFixedFieldsKey}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {pair.key === 'PRIVATE_KEY' ? (
                                        <Input
                                          type="textarea"
                                          rows={5}
                                          name={`key_contents_json_array[${index}].value`}
                                          value={pair.value}
                                          onChange={handleChange}
                                          placeholder="-----BEGIN PRIVATE KEY-----"
                                        />
                                      ) : (
                                        <Input
                                          type="text"
                                          name={`key_contents_json_array[${index}].value`}
                                          value={pair.value}
                                          onChange={handleChange}
                                          placeholder="Value"
                                        />
                                      )}
                                    </TableCell>
                                    {!isFixedFieldsKey && (
                                      <TableCell>
                                        <Button
                                          type="button"
                                          variant="text"
                                          style={KEY_CONTENTS_ROW_BTN_STYLE}
                                          onClick={() => remove(index)}
                                        >
                                          刪除
                                        </Button>
                                      </TableCell>
                                    )}
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {!isFixedFieldsKey && (
                          <Button
                            className="btn btn-cancel"
                            variant="outline"
                            onClick={() => push({ key: '', value: '' })}
                            style={{ marginTop: '20px' }}
                          >
                            新增
                          </Button>
                        )}
                      </>
                    )}
                  </FieldArray>
                </div>
            </Modal.Body>
              <Modal.Footer className="modal-footer">
                <Button
                  type="button"
                  className="btn btn-cancel"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  取消
                </Button>
                <Button type="submit" className="btn btn-custom" disabled={isSubmitting}>
                  儲存
                </Button>
              </Modal.Footer>
            </Form>
            );
          }}
      </Formik>
      </Modal>
    </>
  );
};

export default CloudConfigKeyEditModal;
